import React from "/vendor/react";
import PropTypes from "prop-types";
import gql from "/vendor/graphql-tag";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  withStyles,
} from "/vendor/@material-ui/core";

import {
  useApolloClient,
  useBreakpoint,
  useQuery,
} from "/lib/component/util";
import { Loader } from "/lib/component/base";
import { useManageResourceStatusToast } from "/lib/component/toast";

import putWrapped from "/lib/mutation/putWrapped";

const OUTPUT_METRIC_FORMATS = [
  ["", "None"],
  ["nagios_perfdata", "Nagios (nagios_perfdata)"],
  ["graphite_plaintext", "Graphite (graphite_plaintext)"],
  ["influxdb_line", "InfluxDB (influxdb_line)"],
  ["opentsdb_line", "OpenTSDB (opentsdb_line)"],
  ["prometheus_text", "Prometheus (prometheus_text)"],
];

// Split a textarea/text value into a clean list of strings. Accepts either
// newline or comma separated values.
const parseList = (value) =>
  (value || "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

// Split a textarea into a list preserving each non-empty line (used for
// env_vars which are "KEY=VALUE" strings that may themselves contain commas).
const parseLines = (value) =>
  (value || "")
    .split(/\n/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

// Parse a "key: value" (or "key=value") per line textarea into an object.
const parseKeyVals = (value) => {
  const result = {};
  parseLines(value).forEach((line) => {
    const match = line.match(/^([^:=]+)[:=](.*)$/);
    if (match) {
      result[match[1].trim()] = match[2].trim();
    }
  });
  return result;
};

const keyValsToText = (obj) =>
  Object.keys(obj || {})
    .map((key) => `${key}: ${obj[key]}`)
    .join("\n");

const toInt = (value) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
};

const emptyValues = (namespace) => ({
  name: "",
  command: "",
  subscriptions: "",
  handlers: "",
  interval: "60",
  cron: "",
  timeout: "",
  ttl: "",
  publish: true,
  roundRobin: false,
  stdin: false,
  highFlapThreshold: "",
  lowFlapThreshold: "",
  proxyEntityName: "",
  runtimeAssets: "",
  outputMetricFormat: "",
  outputMetricHandlers: "",
  envVars: "",
  labels: "",
  annotations: "",
  advanced: "",
  __baseSpec: {},
  __baseMeta: { namespace },
});

const valuesFromResource = (json) => {
  // CheckConfig.toJSON returns a wrapped resource:
  //   { type, api_version, metadata: {...}, spec: {...} }
  // so the actual check fields live under `spec`.
  const meta = (json && json.metadata) || {};
  const baseSpec = { ...((json && json.spec) || {}) };
  delete baseSpec.metadata;

  const list = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
  const num = (v) => (v ? String(v) : "");

  return {
    name: meta.name || "",
    command: baseSpec.command || "",
    subscriptions: list(baseSpec.subscriptions),
    handlers: list(baseSpec.handlers),
    interval: num(baseSpec.interval),
    cron: baseSpec.cron || "",
    timeout: num(baseSpec.timeout),
    ttl: num(baseSpec.ttl),
    publish: !!baseSpec.publish,
    roundRobin: !!baseSpec.round_robin,
    stdin: !!baseSpec.stdin,
    highFlapThreshold: num(baseSpec.high_flap_threshold),
    lowFlapThreshold: num(baseSpec.low_flap_threshold),
    proxyEntityName: baseSpec.proxy_entity_name || "",
    runtimeAssets: list(baseSpec.runtime_assets),
    outputMetricFormat: baseSpec.output_metric_format || "",
    outputMetricHandlers: list(baseSpec.output_metric_handlers),
    envVars: list(baseSpec.env_vars),
    labels: keyValsToText(meta.labels),
    annotations: keyValsToText(meta.annotations),
    advanced: "",
    __baseSpec: baseSpec,
    __baseMeta: meta,
  };
};

const buildRaw = (values, namespace) => {
  const spec = { ...values.__baseSpec };

  spec.command = values.command;
  spec.subscriptions = parseList(values.subscriptions);
  spec.handlers = parseList(values.handlers);

  if (values.cron.trim()) {
    spec.cron = values.cron.trim();
    spec.interval = 0;
  } else {
    spec.interval = toInt(values.interval);
    spec.cron = "";
  }

  spec.timeout = toInt(values.timeout);
  spec.ttl = toInt(values.ttl);
  spec.publish = !!values.publish;
  spec.round_robin = !!values.roundRobin;
  spec.stdin = !!values.stdin;
  spec.high_flap_threshold = toInt(values.highFlapThreshold);
  spec.low_flap_threshold = toInt(values.lowFlapThreshold);
  spec.proxy_entity_name = values.proxyEntityName;
  spec.runtime_assets = parseList(values.runtimeAssets);
  spec.output_metric_format = values.outputMetricFormat;
  spec.output_metric_handlers = parseList(values.outputMetricHandlers);
  spec.env_vars = parseLines(values.envVars);

  // Advanced overrides win: merge arbitrary spec keys supplied as raw JSON.
  if (values.advanced.trim()) {
    Object.assign(spec, JSON.parse(values.advanced));
  }

  const metadata = { ...values.__baseMeta };
  metadata.name = values.name.trim();
  metadata.namespace = namespace;
  metadata.labels = parseKeyVals(values.labels);
  metadata.annotations = parseKeyVals(values.annotations);

  return JSON.stringify({
    type: "CheckConfig",
    api_version: "core/v2",
    metadata,
    spec,
  });
};

const StyledContentText = withStyles(() => ({
  root: { marginBottom: "1.5rem" },
}))(DialogContentText);

const CheckConfigForm = ({ mode, namespace, initialValues, onClose, onSuccess }) => {
  const client = useApolloClient();
  const createToast = useManageResourceStatusToast();
  const fullScreen = !useBreakpoint("sm", "gt");

  const [values, setValues] = React.useState(initialValues);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const isEdit = mode === "edit";

  const field = (key) => ({
    value: values[key],
    onChange: (ev) => setValues((v) => ({ ...v, [key]: ev.target.value })),
  });

  const toggle = (key) => ({
    checked: values[key],
    onChange: (ev) => setValues((v) => ({ ...v, [key]: ev.target.checked })),
  });

  const validate = () => {
    if (!isEdit && !values.name.trim()) {
      return "Name is required.";
    }
    if (!isEdit && !/^[A-Za-z0-9_.\-]+$/.test(values.name.trim())) {
      return "Name may only contain letters, numbers, and . _ - characters.";
    }
    if (!values.command.trim()) {
      return "Command is required.";
    }
    if (!values.cron.trim() && toInt(values.interval) <= 0) {
      return "Provide either an interval (seconds) or a cron expression.";
    }
    if (values.advanced.trim()) {
      try {
        const parsed = JSON.parse(values.advanced);
        if (typeof parsed !== "object" || Array.isArray(parsed)) {
          return "Advanced overrides must be a JSON object.";
        }
      } catch (err) {
        return `Advanced overrides are not valid JSON: ${err.message}`;
      }
    }
    return null;
  };

  const submit = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

    const raw = buildRaw(values, namespace);
    // New checks must not silently overwrite an existing resource.
    const upsert = isEdit;

    putWrapped(client, { raw, upsert })
      .then((result) => {
        if (result && result.errors && result.errors.length > 0) {
          const first = result.errors[0];
          throw new Error(first.message || first.code || "Request failed");
        }

        createToast(Promise.resolve(result), {
          action: isEdit ? "edit" : "create",
          resourceType: "check",
          resourceName: values.name.trim(),
        });

        onSuccess();
        onClose();
      })
      .catch((err) => {
        setSubmitting(false);
        const networkError = err && err.networkError;
        setError(
          (networkError && networkError.message) ||
            (err && err.message) ||
            "Request failed",
        );
      });
  };

  const close = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog open fullScreen={fullScreen} maxWidth="md" fullWidth onClose={close}>
      <Loader loading={submitting} passthrough>
        <DialogTitle>{isEdit ? `Edit check ${initialValues.name}` : "New check"}</DialogTitle>
        <DialogContent>
          <StyledContentText>
            Define how, when and where a check runs. Fields left blank fall back
            to Sensu defaults. Anything not covered by the form below can be set
            through the raw JSON overrides at the bottom.
          </StyledContentText>

          {error && (
            <Typography color="error" paragraph>
              {error}
            </Typography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                required
                disabled={isEdit}
                helperText={isEdit ? "Check name cannot be changed." : undefined}
                {...field("name")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Namespace" fullWidth disabled value={namespace} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Command"
                fullWidth
                required
                multiline
                {...field("command")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Subscriptions"
                fullWidth
                multiline
                helperText="One per line (or comma separated)."
                {...field("subscriptions")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Handlers"
                fullWidth
                multiline
                helperText="One per line (or comma separated)."
                {...field("handlers")}
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: "1.5rem 0" }} />
          <Typography variant="subtitle1" gutterBottom>
            Scheduling
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Interval (seconds)"
                type="number"
                fullWidth
                helperText="Used when no cron is set."
                {...field("interval")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Cron"
                fullWidth
                helperText="e.g. */5 * * * * (overrides interval)."
                {...field("cron")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Timeout (seconds)"
                type="number"
                fullWidth
                {...field("timeout")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="TTL (seconds)"
                type="number"
                fullWidth
                {...field("ttl")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="High flap threshold"
                type="number"
                fullWidth
                {...field("highFlapThreshold")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Low flap threshold"
                type="number"
                fullWidth
                {...field("lowFlapThreshold")}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginTop: "0.5rem" }}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox {...toggle("publish")} />}
                label="Published"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox {...toggle("roundRobin")} />}
                label="Round robin"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox {...toggle("stdin")} />}
                label="Accepts STDIN"
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: "1.5rem 0" }} />
          <Typography variant="subtitle1" gutterBottom>
            Assets, proxy and metrics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Runtime assets"
                fullWidth
                multiline
                helperText="One per line (or comma separated)."
                {...field("runtimeAssets")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Proxy entity name"
                fullWidth
                {...field("proxyEntityName")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Output metric format</InputLabel>
                <Select {...field("outputMetricFormat")}>
                  {OUTPUT_METRIC_FORMATS.map(([value, label]) => (
                    <MenuItem key={value || "none"} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Set when the check emits metrics.
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Output metric handlers"
                fullWidth
                multiline
                helperText="One per line (or comma separated)."
                {...field("outputMetricHandlers")}
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: "1.5rem 0" }} />
          <Typography variant="subtitle1" gutterBottom>
            Environment and metadata
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Environment variables"
                fullWidth
                multiline
                helperText="One KEY=VALUE per line."
                {...field("envVars")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Labels"
                fullWidth
                multiline
                helperText="One key: value per line."
                {...field("labels")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Annotations"
                fullWidth
                multiline
                helperText="One key: value per line."
                {...field("annotations")}
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: "1.5rem 0" }} />
          <Typography variant="subtitle1" gutterBottom>
            Advanced (raw JSON spec overrides)
          </Typography>
          <TextField
            label="Spec overrides (JSON)"
            fullWidth
            multiline
            rows={4}
            placeholder={'{ "proxy_requests": { "entity_attributes": [] } }'}
            helperText="Merged into the check spec. Use snake_case keys, e.g. check_hooks, proxy_requests, secrets, pipelines."
            {...field("advanced")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button
            autoFocus
            color="primary"
            variant="contained"
            disabled={submitting}
            onClick={submit}
          >
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Loader>
    </Dialog>
  );
};

CheckConfigForm.propTypes = {
  mode: PropTypes.oneOf(["new", "edit"]).isRequired,
  namespace: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

const editCheckQuery = gql`
  query CheckConfigDialogQuery($namespace: String!, $name: String!) {
    check(namespace: $namespace, name: $name) {
      id
      toJSON
    }
  }
`;

const EditCheckLoader = ({ namespace, name, onClose, onSuccess }) => {
  const query = useQuery({
    query: editCheckQuery,
    variables: { namespace, name },
    fetchPolicy: "network-only",
  });

  const { data = {}, networkStatus } = query;
  const loading = networkStatus < 6;

  if (loading && !data.check) {
    return (
      <Dialog open onClose={onClose}>
        <DialogTitle>Edit check {name}</DialogTitle>
        <DialogContent>
          <Loader loading>
            <div style={{ minHeight: 80 }} />
          </Loader>
        </DialogContent>
      </Dialog>
    );
  }

  if (!data.check) {
    return null;
  }

  return (
    <CheckConfigForm
      mode="edit"
      namespace={namespace}
      initialValues={valuesFromResource(data.check.toJSON)}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
};

const CheckConfigDialog = ({ namespace, checkName, onClose, onSuccess }) => {
  if (checkName) {
    return (
      <EditCheckLoader
        namespace={namespace}
        name={checkName}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <CheckConfigForm
      mode="new"
      namespace={namespace}
      initialValues={emptyValues(namespace)}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
};

CheckConfigDialog.propTypes = {
  namespace: PropTypes.string.isRequired,
  checkName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

CheckConfigDialog.defaultProps = {
  checkName: null,
  onSuccess: () => null,
};

export default CheckConfigDialog;
